import React, { useEffect } from 'react'
import LoadingPageV2 from '../LoadingPageV2'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import ReportsFeature from '../../features/ReportsFeatureV2'
import ROUTES from '../../consts/paths'
import SkipNavigation from '../../components/SkipNavigation'
import { notification } from '../../components/Notifications'
import { Container, Stack } from '@mui/material'
import ReportsControls from '../../features/ReportsFeatureV2/components/ReportsControls'
import PageHeader from '../../components/PageHeader'
import { prepareBreadCrumbs } from '../../utils/BreadcrumbDataPreparation'
import ReportAttributes from '../../features/ReportsFeatureV2/components/ReportAttributes'
import { DivisionManager } from '../../APIManager'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { saveAs } from 'file-saver'
import { formatReportPayload, handleReportNotifications } from '../../features/ReportsFeatureV2/components/helper'
import { ReportsActionType, ReportsProvider, useReportsContext } from '../../features/ReportsFeatureV2/ReportsContext'
import { keyStatSwitches } from '../../features/ReportsFeatureV2/components/constants'

import { ProjectAndTermHeader } from '../../features/ReportsFeatureV2/components/ProjectAndTermHeader'
import { AnonymousSurveyChip } from '../../features/ReportsFeatureV2/components/AnonymousSurveyChip'

const ReportsPage: React.FC = (): JSX.Element => {
  const { isFullScreen, selectedTrend, dispatch, displaySettings } = useReportsContext()

  const { id: divisionId } = useParams()
  const [searchParams] = useSearchParams()
  const seriesId = searchParams.get('projectId')
  const termId = searchParams.get('termId')
  const params = { termId, distributionSeriesId: seriesId, pageSize: 200 }

  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const divisionAndSections = queryClient.getQueryData<any[]>(['topBarDivisions'])

  useEffect(() => {
    const firstDivisionId = divisionAndSections?.[0]?.divisionId ?? null
    if (!divisionId && firstDivisionId) {
      navigate({
        pathname: `${ROUTES.REPORTS}/${firstDivisionId}`,
        search: searchParams.toString()
      })
    }
  }, [divisionAndSections])

  // On division ID change, reset the 'comparisons' and 'slices' fields in displaySettings in context.
  useEffect(() => {
    if (divisionId) {
      dispatch({
        type: ReportsActionType.SET_DISPLAY_SETTINGS,
        payload: {
          ...displaySettings,
          comparisons: [{ compareBy: null, comparison: null }],
          slices: []
        }
      })
    }
  }, [divisionId, selectedTrend])

  const { data: breadCrumbData, isLoading: breadcrumbLoading } = useQuery({
    queryKey: ['reportsBreadcrumbs', divisionId],
    queryFn: async () => await DivisionManager.getBreadCrumbList(divisionId ?? ''),
    enabled: !!divisionId
  })

  const preparedBreadCrumbList = prepareBreadCrumbs(breadCrumbData ?? [], ROUTES.REPORTS, searchParams)

  const { mutate: fetchReports, data: reports = [], error: reportError, isError: isReportsError, isPending: isReportsPending } = useMutation({
    mutationFn: async (payload?: Record<string, any>) => {
      const calc = Object.keys(payload ?? {}).length > 0 ? payload : formatReportPayload(displaySettings)
      const safeData = calc ?? {}
      let res
      if (selectedTrend > 1) {
        res = await DivisionManager.getHistoricalTrendsReports({
          params,
          divisionId: divisionId ?? '',
          data: {
            limit: selectedTrend,
            calculations: keyStatSwitches.map(s => s.name)
          }
        })
      } else {
        res = await DivisionManager.getReportsCompare({
          params,
          divisionId: divisionId ?? '',
          data: safeData as { calculations?: string[], compareIds?: string[] }
        })
      }
      return res
    },
    onSuccess: (data: any, payload) => { }
  })

  const { data: completionRate = null, isError: isCompletionRateError, error: completionRateError, isLoading: completionRateLoading } = useQuery({
    queryKey: ['completionRate', divisionId, seriesId, termId],
    queryFn: async () => (await DivisionManager.getCompletionRate(divisionId ?? '', params)),
    enabled: !!divisionId && !!seriesId && !!termId,
    retry: false
  })

  const downloadFile = async (fileType: string): Promise<void> => {
    const divisionId = breadCrumbData?.[breadCrumbData.length - 1]?.id
    if (!divisionId) return

    try {
      const fetchFile = fileType === 'csv'
        ? DivisionManager.getDivisionCSVExport
        : DivisionManager.getDivisionPDFExport

      const result = await fetchFile(divisionId, params)
      const blob = new Blob([result], {
        type: fileType === 'csv' ? 'text/csv' : 'application/pdf'
      })

      saveAs(blob, `report-${divisionId}.${fileType}`)
    } catch (err) {
      notification(`${fileType.toUpperCase()} export error`, {
        variant: 'error',
        subTitle: `An error occurred while trying to export the ${fileType.toUpperCase()}`
      })
    }
  }

  useEffect(() => {
    handleReportNotifications(
      reportError,
      completionRateError,
      isReportsError,
      isCompletionRateError,
      isReportsPending,
      completionRateLoading
    )
  }, [
    reportError?.status,
    completionRateError?.status,
    isReportsError,
    isCompletionRateError
  ])

  useEffect(() => {
    if (divisionId && seriesId && termId) {
      fetchReports({})
    }
  }, [divisionId, seriesId, termId, selectedTrend])

  // Add loader for mutation API
  if (breadcrumbLoading || completionRateLoading) {
    return <LoadingPageV2 title={PAGE_TITLES.REPORTS} />
  }

  const controls = <ReportsControls
    disableButtons={!completionRate}
    downloadPDFReport={async () => { await downloadFile('pdf') }}
    downloadCSVFile={async () => { await downloadFile('csv') }}
    reports={isReportsError ? [] : reports}
    fetchReports={fetchReports}
    setIsFullScreen={(value) => {
      const resolvedValue = typeof value === 'function' ? value(isFullScreen) : value
      dispatch({ type: ReportsActionType.SET_IS_FULLSCREEN, payload: resolvedValue })
    }}

    isFullScreen={isFullScreen}
  />

  const disabledProjectAndTermHeader = String(completionRate?.categoryName) === 'Section'

  return (
    <>
      <CustomHeader info={preparedBreadCrumbList?.at(-1)?.title ?? PAGE_TITLES.REPORTS} />
      <SkipNavigation />
      <Container
        maxWidth={isFullScreen ? false : 'lg'}
        disableGutters={isFullScreen}
      >
        <Stack>
          <PageHeader
            breadcrumb={preparedBreadCrumbList}
            actions={<ProjectAndTermHeader disabledProjectAndTermHeader={disabledProjectAndTermHeader} />}
            attributes={completionRate && <ReportAttributes completionRate={completionRate} parentSurveyScore={completionRate?.surveyScore} />}
            title={completionRate?.title}
            controls={controls}
            statusChip={<AnonymousSurveyChip show={completionRate?.isAnonymousSurvey ?? false} />}
          />
          <ReportsFeature
            reports={reports}
            completionRate={completionRate}
            reportError={reportError}
            parentSurveyScore={completionRate?.surveyScore}
            isReportsPending={ isReportsPending}
            isCompletionRateError={isCompletionRateError}
            seriesId={seriesId}
            termId={termId}
          />
        </Stack>
      </Container>
    </>
  )
}

const ReportsPageWithProvider: React.FC = (props) => (
  <ReportsProvider>
    <ReportsPage {...props} />
  </ReportsProvider>
)

export default ReportsPageWithProvider
